import { ABExplorationKeyWords, SupportedMeasureType } from "@src/constant/ab-testing/ab-testing-exploration.enum";
import { IMetricEvaluateData, IRatioMeasureDefinition } from "@src/types/ab-testing-exploration";

const getMeasureNameWithoutPrefix = (measureName: string) => {
    return measureName?.startsWith("ratio_of_") ? measureName.replace("ratio_of_", "") : measureName;
};
export const getMetricEvaluationRequireMeasures = (
    measureName?:
        | string
        | {
              customMean: string;
              customNobs?: string;
              customStd?: string;
          },
    measureType?: string,
    measureDefinition?: IRatioMeasureDefinition,
    includePrimaryMeasure = false,
    includeSampleSize = false,
) => {
    const requiredMeasures: string[] = [];

    if (includePrimaryMeasure && measureName) {
        requiredMeasures.push(typeof measureName === "string" ? measureName : measureName?.customMean);
    }

    if (includeSampleSize) {
        requiredMeasures.push(ABExplorationKeyWords.SAMPLE_SIZE);
    }

    switch (measureType) {
        case SupportedMeasureType.PROPORTION: {
            break;
        }
        case SupportedMeasureType.RATIO: {
            if (!measureDefinition) {
                throw Error(`${SupportedMeasureType.RATIO} require measureDefinition`);
            }
            const { numerator, denominator } = measureDefinition;
            if (measureName) {
                requiredMeasures.push(
                    ...[
                        `${numerator}_per_user`,
                        `var_samp_${numerator}`,
                        `${denominator}_per_user`,
                        `var_samp_${denominator}`,
                        `covar_samp_${getMeasureNameWithoutPrefix(
                            typeof measureName === "string" ? measureName : measureName.customMean,
                        )}`,
                    ],
                );
            }

            break;
        }
        case SupportedMeasureType.MEAN:
        default: {
            if (typeof measureName === "string") {
                requiredMeasures.push(...[`${measureName}_per_user`, `stddev_samp_${measureName}`]);
            } else if (measureName) {
                requiredMeasures.push(measureName?.customMean);
                if (measureName.customNobs) {
                    requiredMeasures.push(measureName.customNobs);
                }
                if (measureName.customStd) {
                    requiredMeasures.push(measureName.customStd);
                }
            }
        }
    }

    const isValidToEvaluate = (data: any): boolean => {
        return requiredMeasures.filter((k) => data[k] === null || data[k] === undefined).length === 0;
        // return Object.keys(data).filter((k: any) => requiredMeasures.findIndex(k) !== -1).length = requiredMeasures.length
    };

    const toEvaluatingData = (variant: string, data: any): IMetricEvaluateData | undefined => {
        // convert to request data
        if (!isValidToEvaluate(data) || !measureName) {
            return undefined;
        }

        switch (measureType) {
            case SupportedMeasureType.PROPORTION: {
                if (typeof measureName === "string") {
                    return {
                        exp_group: variant,
                        mean: data?.[measureName],
                        nobs: data?.[ABExplorationKeyWords.SAMPLE_SIZE],
                    };
                } else {
                    return {
                        exp_group: variant,
                        mean: data?.[measureName.customMean],
                        nobs: data?.[ABExplorationKeyWords.SAMPLE_SIZE],
                    };
                }
            }
            case SupportedMeasureType.RATIO: {
                if (!measureDefinition) {
                    throw Error(`${SupportedMeasureType.RATIO} require measureDefinition`);
                }
                const { numerator, denominator } = measureDefinition;

                return {
                    exp_group: variant,
                    cov:
                        typeof measureName === "string"
                            ? data[`covar_samp_${getMeasureNameWithoutPrefix(measureName)}`]
                            : data[`covar_samp_${getMeasureNameWithoutPrefix(measureName.customMean)}`],
                    nobs: data[ABExplorationKeyWords.SAMPLE_SIZE],
                    mean: typeof measureName === "string" ? data[measureName] : data[measureName.customMean],
                    numer: {
                        mean: data[`${numerator}_per_user`],
                        var: data[`var_samp_${numerator}`],
                    },
                    denom: {
                        mean: data[`${denominator}_per_user`],
                        var: data[`var_samp_${denominator}`],
                    },
                };
            }
            case SupportedMeasureType.MEAN:
            default:
                return {
                    exp_group: variant,
                    mean:
                        typeof measureName === "string"
                            ? data[`${measureName}_per_user`]
                            : data[measureName.customMean],
                    std:
                        typeof measureName === "string"
                            ? data[`stddev_samp_${measureName}`]
                            : measureName.customStd
                            ? data[measureName.customStd]
                            : undefined,
                    nobs: data[ABExplorationKeyWords.SAMPLE_SIZE],
                };
        }
    };

    return {
        requiredMeasures,
        toEvaluatingData,
        isValidToEvaluate,
    };
};
